import { Link } from '@remix-run/react';
import { json, type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import {
  ArrowRightIcon,
  ScaleIcon,
  ExclamationTriangleIcon,
  HomeIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import Header from '~/components/Header';
import Footer from '~/components/Footer';
import {
  generateCanonicalUrl,
  generateBreadcrumbJsonLd,
  SITE_NAME,
} from '~/utils/seo';
import FAQ from '~/components/FAQ';
import { BLOG_CACHE_HEADERS } from '~/utils/cacheHeaders';
import { getAuthenticatedUser } from '~/utils/supabase.server';

export const maxDuration = 300;

const SLUG = 'user-generated-vs-brand-generated-content';
const TITLE = 'User-Generated vs Brand-Generated Content: Which Should You Make More Of?';
const HERO_IMAGE = '/blog/user-generated-vs-brand-generated-content-hero.jpg';
const HERO_IMAGE_WEBP = '/blog/user-generated-vs-brand-generated-content-hero.webp';
const HERO_IMAGE_AVIF = '/blog/user-generated-vs-brand-generated-content-hero.avif';

export const meta: MetaFunction = () => {
  const canonicalUrl = generateCanonicalUrl(`/blog/${SLUG}`);
  const metaTitle = 'User-Generated vs Brand-Generated Content';
  const description =
    'When to lead with brand-generated content and when UGC wins: trust data read carefully, where brand control beats UGC, and claim-and-proof stacking for B2B launches.';

  return [
    { title: `${metaTitle} | ${SITE_NAME}` },
    { name: 'description', content: description },
    {
      name: 'keywords',
      content:
        'user generated content vs brand content, UGC vs brand content, brand generated content, UGC for B2B, claim and proof stacking, UGC trust statistics, social proof marketing',
    },
    { property: 'og:title', content: `${metaTitle} | ${SITE_NAME}` },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:image', content: generateCanonicalUrl(HERO_IMAGE) },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: `${metaTitle} | ${SITE_NAME}` },
    { name: 'twitter:description', content: description },
    { tagName: 'link', rel: 'canonical', href: canonicalUrl },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await getAuthenticatedUser(request);

  return json({ isLoggedIn: !!user }, { headers: BLOG_CACHE_HEADERS });
}

const FAQS = [
  {
    question: 'What is the difference between user-generated content and brand-generated content?',
    answer:
      'User-generated content is made by customers or other users, such as reviews, comments, demo videos, and photos. Brand-generated content is made or commissioned by the company, such as explainers, announcements, and campaign creative. The practical difference is control versus credibility: brand content says exactly what you approve, while UGC carries the believability of someone who has no stake in your marketing, as long as it is genuinely organic.',
  },
  {
    question: 'How is UGC different from influencer content?',
    answer:
      'Organic UGC comes from customers who were not paid to post. Influencer content and paid UGC creator videos are commissioned, even when they look casual. Sprout Social\'s 2025 data found user comments only slightly more trusted than influencer recommendations, 38% versus 35%. The bigger risk is disclosure: paid content presented as organic can damage trust, so label each piece according to how it was produced.',
  },
  {
    question: 'Is UGC more trusted than brand content?',
    answer:
      'In surveys, yes. Emplifi\'s 2026 research found 63% of consumers trust user-generated reviews, compared with 38% for social media ads. But those surveys measure trust, not sales, and they use different audiences. Brand content still wins when accuracy and detailed explanation matter, such as security claims, pricing changes, or technical walkthroughs that a customer post cannot cover reliably.',
  },
  {
    question: 'Do I need permission to repost customer content?',
    answer:
      'Yes. Get explicit permission before reusing any customer post, review, image, or video in marketing or advertising, and keep a record of that consent. Credit the creator as agreed. Check the content for third-party material such as background music or other people\'s images, because licensing those elements is a separate question from the customer\'s own consent.',
  },
  {
    question: 'Is UGC cheaper than brand-generated content?',
    answer:
      'The cash cost is usually lower, but it is not free. Collection tools like Later\'s Growth plan run $37.50 a month billed yearly, and the hidden costs include chasing permissions, legal review, and reformatting assets for each platform. Paid creator content often costs about as much as brand production. Organic UGC is the cheap option, and you cannot schedule when it arrives.',
  },
];

const LABELS: [string, string][] = [
  ['Organic UGC', 'a customer posted it on their own, unprompted.'],
  ['Incentivized content', 'a customer posted it after receiving a discount, gift, or entry into a draw.'],
  [
    'Paid creator content',
    'a UGC creator or influencer was paid to make it. Influencer marketing sits here, as does the newer category of creators who produce "customer-style" videos for a brand\'s ads.',
  ],
  ['Brand-produced content', 'your team or agency made it.'],
  ['AI-generated content', 'a subtype of brand content, not a synonym for it.'],
];

const TOOL_ROWS: [string, string, string][] = [
  [
    'Thetabber',
    'Publishing brand content and customer proof to 9+ platforms from one schedule, with AI captions and UGC videos',
    'See pricing',
  ],
  ['Later', 'Visual planning and publishing with UGC collection on Growth', '$18.75/mo (yearly)'],
  ['Yotpo Reviews & UGC', 'Collecting and displaying reviews for ecommerce', 'Free, paid from $15/mo'],
  ['Metricool', 'Scheduling plus analytics and reporting', 'See site'],
  ['Buffer', 'Straightforward scheduling and collaboration', 'See site'],
  ['Publer', 'Bulk scheduling and automation', 'See site'],
  ['SocialBee', 'Content categories and evergreen recycling', 'See site'],
];

const RECAP_ROWS: [string, string, string][] = [
  [
    'Brand-generated content',
    'Launches, positioning, technical explainers, regulated claims',
    'Team time plus production and review',
  ],
  ['Organic UGC', 'Proof, objection handling, product pages, retargeting', 'Low cash cost, real permissioning time'],
  ['Incentivized UGC', 'Building review volume quickly', 'Incentive cost plus disclosure management'],
  ['Paid creator content', 'Ad creative with a peer look', 'Creator fees, priced like brand content'],
  ['Combined (claim-and-proof)', 'Most B2B launches and campaigns', 'Publishing tool plus modest collection effort'],
];

const UGC_STEPS = [
  'Audit what already exists: reviews, G2 or Capterra comments, customer tweets, support-call praise.',
  'Write a consent and attribution policy, including how you will label organic, incentivized, and paid content.',
  'Pick three to five claims from your brand messaging that most need proof.',
  'Ask specific customers for specific evidence tied to those claims, rather than a generic "share your experience."',
  'Publish each asset next to the brand claim it supports, and track assisted conversions, not just engagement.',
];

const extLink = 'text-gray-900 underline hover:text-gray-700';

export default function UgcVsBrandContentPage() {
  const { isLoggedIn } = useLoaderData<typeof loader>();

  const breadcrumbJsonLd = generateBreadcrumbJsonLd([
    { name: 'Home', url: generateCanonicalUrl('/') },
    { name: 'Blog', url: generateCanonicalUrl('/blog') },
    {
      name: 'User-Generated vs Brand-Generated Content',
      url: generateCanonicalUrl(`/blog/${SLUG}`),
    },
  ]);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: TITLE,
    description:
      'When to lead with brand-generated content and when user-generated content wins, with the trust data read carefully and a claim-and-proof stacking pattern for B2B launches.',
    image: generateCanonicalUrl(HERO_IMAGE),
    datePublished: '2026-11-01',
    author: { '@type': 'Organization', name: SITE_NAME },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: generateCanonicalUrl('/'),
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />

      <Header variant="static" isLoggedIn={isLoggedIn} />

      {/* Breadcrumbs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <nav className="flex items-center text-sm text-gray-500" aria-label="Breadcrumb">
          <Link to="/" className="flex items-center hover:text-gray-700 transition-colors">
            <HomeIcon className="w-4 h-4 mr-1" />
            Home
          </Link>
          <ChevronRightIcon className="w-4 h-4 mx-2" />
          <Link to="/blog" className="hover:text-gray-700 transition-colors">
            Blog
          </Link>
          <ChevronRightIcon className="w-4 h-4 mx-2" />
          <span className="text-gray-900 font-medium">UGC vs Brand-Generated Content</span>
        </nav>
      </div>

      {/* Hero Section */}
      <section className="relative py-12 lg:py-20 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.4]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgb(156 163 175) 1px, transparent 0)`,
            backgroundSize: '24px 24px',
            maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 mb-6">
              <ScaleIcon className="w-5 h-5 text-gray-700" />
              <span className="text-sm font-semibold text-gray-700">Content Strategy</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-6 leading-tight">
              {TITLE}
            </h1>

            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              You know the scene. Product marketing spends three weeks on a launch video: scripted,
              color-graded, signed off by legal and the CEO. It ships, gets polite engagement, and then
              a customer posts a blurry screenshot in a Reddit thread saying the new feature cut their
              audit prep in half. That screenshot is what prospects forward to their boss.
            </p>

            <p className="mt-6 text-sm text-gray-500">Nov 1, 2026 · 13 min read</p>
          </div>
        </div>
      </section>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <figure className="mb-12 not-prose">
          <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-lg">
            <picture>
              <source srcSet={HERO_IMAGE_AVIF} type="image/avif" />
              <source srcSet={HERO_IMAGE_WEBP} type="image/webp" />
              <img
                src={HERO_IMAGE}
                alt="User-generated vs brand-generated content"
                className="w-full"
              />
            </picture>
          </div>
        </figure>

        <article className="prose prose-lg max-w-none">
          <p className="text-lg text-gray-600 mb-6 leading-relaxed">
            So which should you make more of? Use brand-generated content when you need to explain,
            announce, or control a claim. Use user-generated content (UGC) when you need to prove that
            the claim holds up in someone else's hands.
          </p>
          <p className="text-gray-600 mb-10 leading-relaxed">
            For most B2B software teams, the winner is a combination, and the order matters. Brand
            content leads because it carries the message accurately. Customer evidence follows close
            behind because it carries the belief. If you sell to consumers and the purchase hinges on
            "does this work for people like me," UGC can take the lead. If you sell a technical product
            where one wrong sentence becomes a support ticket or a compliance problem, brand content
            stays in charge and customer proof plays a supporting role.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">The short version</h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Brand-generated content wins on clarity, control, and accuracy: launches, positioning,
            feature explainers, anything legal will read. UGC wins on credibility and lived experience:
            reviews, customer posts, and demos that answer objections close to the purchase. The
            best-performing setup we see pairs the two. The brand makes the claim and a labeled,
            permissioned customer voice backs it up. Neither format beats the other everywhere. Each
            wins at a specific job.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">
            Five labels that get lumped together
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Brand-generated content is anything the company produces or commissions: product
            explainers, feature announcements, thought-leadership articles, campaign creative, branded
            social posts, professionally shot video. User-generated content is created by customers or
            other users. That covers reviews, testimonials, comments, unboxing videos, product
            demonstrations, community posts, and customer photos.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            The confusion starts in the gray zone, and it changes how much credibility a piece of
            content has earned. A paid creator deliverable is not an unsolicited customer review, even
            when it looks identical on TikTok. Before you publish anything, sort it into one of these
            buckets:
          </p>
          <ul className="space-y-3 mb-6 not-prose">
            {LABELS.map(([label, desc]) => (
              <li key={label} className="flex gap-2 text-gray-700 leading-relaxed">
                <span className="text-gray-400">•</span>
                <span>
                  <span className="font-semibold text-gray-900">{label}:</span> {desc}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-gray-600 mb-6 leading-relaxed">
            That last distinction matters more than most guides admit. In Emplifi's 2026 consumer
            survey, only 31% of respondents saw AI-generated content as authentic. Read carelessly, that
            number gets stretched into "consumers distrust brand content," and that conclusion does not
            follow from the data. A product manager's narrated walkthrough is brand content. So is a
            generic AI image of a smiling team at a laptop. Audiences treat them very differently.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">
            The trust numbers, read carefully
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            The headline figures favor UGC. Emplifi found that 63% of consumers trust user-generated
            reviews, against 42% for marketing emails and 38% for social media ads. Reviews also sit
            squarely in the buying process: 79% of the same respondents read at least three before
            purchasing anything. More than eight in ten said they would pay more for brands they see as
            authentic.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Social platforms tell a similar story with a narrower margin.{' '}
            <a
              href="https://sproutsocial.com/insights/press/social-media-overtakes-traditional-channels-for-holiday-shopping-inspiration/"
              target="_blank"
              rel="noopener noreferrer"
              className={extLink}
            >
              Sprout Social's Q4 2025 Pulse Survey
            </a>{' '}
            found user comments were the most trusted social-shopping input at 38%, only three points
            ahead of influencer recommendations at 35%. That gap is small. It is not a mandate to fire
            your creators. Social media is also where discovery happens: nearly half of users (45%)
            cite it as a source of product ideas, per the same Sprout data.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Then there is the AI factor, which is pushing audiences toward visible humans. In a 2026
            survey,{' '}
            <a
              href="https://www.gartner.com/en/newsroom/press-releases/2026-09-22-gartner-marketing-survuey-finds-35-percent-of-consumers-rely-on-influencers-less-due-to-ai"
              target="_blank"
              rel="noopener noreferrer"
              className={extLink}
            >
              Gartner
            </a>{' '}
            put the share of U.S. consumers who think brands produce too much AI marketing content at
            65%, and well over half said it has made them less trusting of brand messaging overall. The
            shift does not run in one direction, though. As AI content spreads, 43% say they lean more
            on real people for shopping information, while 35% say they rely on influencers less. Real
            people gain ground and paid people lose it, so the label on the content matters as much as
            the face in it.
          </p>

          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-5 my-6 not-prose">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-gray-700 text-sm leading-relaxed">
              <span className="font-semibold text-gray-900">Warning: </span>
              These three surveys used different questions and different audiences. Treat them as
              directional evidence about trust and discovery, not as proof that UGC lifts sales. Trust,
              discovery, engagement, and conversion are separate outcomes, so don't put a trust
              percentage in a revenue forecast.
            </p>
          </div>

          <p className="text-gray-600 mb-6 leading-relaxed">
            On r/NoStupidQuestions, a recurring thread asks why casual posts, screenshots, and Reddit
            comments feel more honest than polished brand posts even when people suspect some are
            sponsored. The answer users keep landing on is peer relatability and firsthand context.
            Production quality and disclosure matter, but they come second to "someone like me used
            this."
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">
            Where polished brand control beats UGC
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Most comparison pages skip this part. There are jobs where UGC is the wrong tool, and using
            it anyway costs you.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Announcements are the obvious case. A funding round, a pricing change, a new security
            certification, or a deprecation notice needs one accurate version of the facts, published
            everywhere at the same moment. No customer can do that for you. Positioning is another. If
            you are moving from "monitoring tool" to "platform," your category story has to be told in
            your words, repeatedly and consistently, before any customer will repeat it back.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Technical accuracy is the case that matters most for software companies. A customer video
            that says your product "encrypts everything automatically" might be enthusiastic and wrong,
            and a prospect's security team will hold you to it. Brand content goes through product and
            legal review. A customer's TikTok does not. When accuracy, consistency, compliance, or a
            detailed product explanation matters more than relatability, brand-generated content should
            own the message.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            This is also where the fair objection about AI captions belongs. Generated copy can
            introduce technical claims your product team never approved. The fix is process, not
            avoidance. Use AI to draft platform-specific variants of a message a human already approved,
            then route anything with a factual claim back through review. Our{' '}
            <Link to="/features" className="text-gray-900 font-semibold hover:underline">
              AI captions
            </Link>{' '}
            are built for that drafting step, turning one approved announcement into versions sized for
            LinkedIn, X, and YouTube. They do not replace the reviewer who knows which sentences legal
            will strike.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">
            What living with each one feels like
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Brand content has a predictable supply and a slow pipeline. You control the calendar, but
            every post queues behind the same few approvers. Your subject-matter experts have the best
            ideas and the least time, so one webinar insight takes a week to become five channel-specific
            posts. Launches miss their moment while the deck waits for a VP's comments. The output is
            reliable and the bottleneck is people.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            UGC is the opposite: fast and credible when it shows up, unpredictable when you need it. You
            cannot schedule a customer's enthusiasm for launch day. When the perfect post does appear,
            the work starts. You have to ask the creator for permission, record that consent, credit
            them correctly, and check what else is in the frame. Brands should get explicit permission
            before reusing any customer post, review, image, or video in marketing. A clip with a
            popular song in the background brings its own licensing question. Labels are only beginning
            to formalize this:{' '}
            <a
              href="https://www.universalmusic.com/universal-music-group-and-hook-partner-to-expand-licensed-fan-creativity-across-social-media/"
              target="_blank"
              rel="noopener noreferrer"
              className={extLink}
            >
              Universal Music Group's partnership with Hook
            </a>{' '}
            is one attempt to license fan creativity across social platforms. Until those deals cover
            your use case, treat rights management as an operational requirement, not a nicety.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">
            Matching format to funnel stage and category
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Brand-generated content does its best work early in the funnel: awareness, education,
            positioning, and announcements, the moments when a buyer needs to understand what you are.
            UGC earns its keep later, in consideration and objection handling, when the buyer already
            knows what you claim and wants to know whether it is true.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Product category shifts the balance. Consumer products with visual, sensory, or fit
            questions (skincare, apparel, home goods) can let UGC carry the funnel almost end to end,
            because the core objection is "will it work on me." B2B software has a longer path and a
            committee of buyers. The champion is convinced by a peer's post. The CFO is convinced by a
            clear ROI explainer. The security reviewer is convinced by documentation. That mix leans
            toward brand content, with customer proof placed at specific friction points.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">
            Claim-and-proof stacking: the mix that holds up
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            We call the working pattern claim-and-proof stacking. Every piece of brand content that
            makes a claim gets paired with customer evidence that supports it, published close enough
            together that the buyer sees both.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            In practice, a launch looks like this. Day one, the brand ships the explainer, the
            announcement post, and the founder's LinkedIn thread. Over the following two weeks, as early
            users react, you gather permissioned quotes, screenshots, and short demo clips and publish
            them as replies, follow-up posts, carousel slides, and additions to the product page. The
            explainer says "setup takes an afternoon." The customer clip shows someone actually doing it
            in an afternoon.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            This answers the trust gap without giving up control. The brand sets the frame and the
            facts, and the customer voice, clearly labeled as organic, incentivized, or paid, does the
            convincing. It also keeps UGC from drifting off-message, because each customer asset is
            attached to a claim you already approved. Experts who said something smart once become proof
            points across channels, instead of one-off posts that disappear in a day.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">
            The money, worked for one launch
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Take a mid-market SaaS team running the stack many guides recommend: a social scheduler with
            UGC collection, plus a dedicated review platform.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Later's Growth plan, which includes UGC collection, lists at $37.50 a month when billed
            yearly as of 2026. That comes to $450 a year before taxes. Starter runs $18.75 and Scale
            $82.50 on the same yearly terms. Add Yotpo Reviews &amp; UGC, whose paid plans start at $15 a
            month, and the base climbs by another $180 a year. Your floor is around $630 annually, with
            two logins, two permission workflows, and two places to check when someone asks where a
            quote came from.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            There is a catch for B2B teams. Yotpo prices by monthly order volume, and its free plan
            covers up to 50 orders a month. That model fits ecommerce well and fits awkwardly for a
            software company with forty annual contracts, so you end up paying for a framework built
            around someone else's business. Then come the costs no pricing page lists: legal review time
            for each repurposed customer asset, follow-ups chasing creators for consent, and the hours a
            marketer spends reformatting one approved message for every platform. On a lean team, that
            reformatting time usually outweighs the software bill.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">
            Tools for publishing brand content and customer proof
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            The useful distinction is that social schedulers mainly help you distribute content, while
            review and UGC platforms mainly help you collect, manage, permission, and deploy customer
            proof. Few teams need both at full depth.
          </p>
          <div className="overflow-x-auto my-8 not-prose">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="py-3 pr-4 font-semibold text-gray-900">Tool</th>
                  <th className="py-3 pr-4 font-semibold text-gray-900">What it does best</th>
                  <th className="py-3 font-semibold text-gray-900">Starting cost</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {TOOL_ROWS.map((row, i) => (
                  <tr key={i} className="border-b border-gray-100 align-top">
                    <td className="py-3 pr-4 font-medium text-gray-900">{row[0]}</td>
                    <td className="py-3 pr-4">{row[1]}</td>
                    <td className="py-3">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Thetabber comes first on this list because claim-and-proof stacking is a publishing problem
            before it is a collection problem. You need the approved explainer and the customer clips
            going out together, sized correctly for each of more than nine platforms. Our UGC videos
            fill the gap when organic customer content hasn't arrived yet, labeled as brand-commissioned
            rather than passed off as unsolicited reviews. Teams wiring this into internal tooling can
            connect through our{' '}
            <Link to="/agents" className="text-gray-900 font-semibold hover:underline">
              MCP server and API
            </Link>
            .
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Later is the better fit if you sell consumer products on Instagram and want customer-photo
            collection built into the planner. Yotpo is the better fit if you run a high-volume store
            and reviews on product pages are the main event. If you already have HubSpot or Hootsuite
            plus native platform tools, the honest test is whether one approved message reaches every
            channel without someone retyping it. If it does, you may not need to change anything.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">Cases that break the either-or</h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Paid creator content is the biggest muddy case. It borrows UGC's look and costs like brand
            content, and if it is poorly disclosed, it can lose you the trust you were trying to borrow.
            Label it as paid and judge it on creative quality, not on assumed authenticity.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Employee advocacy is another. When your principal engineer posts about a hard migration
            problem the product solved, the post is technically brand-adjacent, yet it reads with UGC's
            firsthand texture. Marketers on X increasingly describe visible human expertise as the
            differentiator now that AI content is everywhere. For B2B companies with strong
            subject-matter experts, this hybrid is often the highest-value content on the calendar, and
            it is the content most often stuck in approvals.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">Measure each by its own job</h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Judge brand content on reach and message recall: did the market hear the positioning, and
            can it repeat it? Judge UGC on review engagement, saves, sentiment, assisted conversions, and
            its influence on the conversion path. A customer clip with modest reach that shows up in a
            closed deal's buyer journey has done its job. Scoring both formats on likes alone will push
            you toward whichever one happens to look better on a feed, and that is rarely the one moving
            pipeline.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">
            Starting a UGC program in five steps
          </h2>
          <ol className="space-y-3 mb-6 list-decimal list-inside not-prose">
            {UGC_STEPS.map((step, i) => (
              <li key={i} className="text-gray-700 leading-relaxed">
                {step}
              </li>
            ))}
          </ol>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">The recap</h2>
          <div className="overflow-x-auto my-8 not-prose">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="py-3 pr-4 font-semibold text-gray-900">Content type</th>
                  <th className="py-3 pr-4 font-semibold text-gray-900">Best for</th>
                  <th className="py-3 font-semibold text-gray-900">Typical cost</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {RECAP_ROWS.map((row, i) => (
                  <tr key={i} className="border-b border-gray-100 align-top">
                    <td className="py-3 pr-4 font-medium text-gray-900">{row[0]}</td>
                    <td className="py-3 pr-4">{row[1]}</td>
                    <td className="py-3">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Choose brand-generated content as your lead if your product is technical, your claims get
            read by legal or security teams, or you are announcing something that must be stated one way
            everywhere at once. Most B2B software companies fall here, and that is no reason to apologize
            for polish.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Choose UGC as your lead if your buyers' main question is "will this work for someone like
            me" and the answer is visual, personal, and quick to show. Consumer brands usually fall here.
            If you asked me as a friend, though, I would tell you to stop picking sides: write the claim
            yourself, get it approved once, and surround it with customers who prove it.
          </p>
        </article>

        <FAQ items={FAQS} align="left" className="mt-12" innerClassName="" />

        <section className="bg-gray-900 text-white rounded-2xl p-8 lg:p-12 mt-16 text-center">
          <h2 className="text-3xl font-bold mb-4">Ship the claim and the proof together</h2>
          <p className="text-lg text-gray-300 mb-6">
            Publish approved brand content and labeled customer proof to 9+ platforms from one schedule,
            with AI captions and UGC videos from TheTabber. Try it free for 3 days.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {isLoggedIn ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all"
              >
                Go to Dashboard
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
            ) : (
              <>
                <Link
                  to="/auth/email/signup"
                  className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all"
                >
                  Start Free Trial
                  <ArrowRightIcon className="w-5 h-5" />
                </Link>
                <Link
                  to="/blog"
                  className="bg-gray-800 text-white px-8 py-4 rounded-xl font-semibold hover:bg-gray-700 transition-all"
                >
                  Read More Guides
                </Link>
              </>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
